import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { Book, BookLanguage, copyRestObject } from '../../../rest/models';
import { deepCopyObject, groupBy, orderBy } from '../../../utils';

function addLanguageToBook(langId: string, state: BookEditorState)
{
    const book = state.formBook;
    const abl = state.__allBookLanguages;
    
    if (!langId)
    {
        return;
    }
    if (getBookLanguageByLangId(book, langId))
    {
        return;
    }

    if (!abl[langId])
    {
        const bl = abl[langId] = copyRestObject(new BookLanguage(), BookLanguage);
        bl.language = langId;
        bl.display_order = ++state.__currentMaxBookLangDisplayOrder;
    }
    
    const bl = abl[langId];
    book.booklanguage_set.push(bl);

    abl[langId] = bl;
}

//First sorts the languages by display_order.
//Then makes sure that __allBookLanguages is up to date.
function sortAndSyncBookLanguages(state: BookEditorState)
{
    const b = state.formBook;

    let bls = b.booklanguage_set;
    bls = orderBy(bls, bl => bl.display_order);
    state.formBook.booklanguage_set = bls;

    bls.forEach((bl) => state.__allBookLanguages[bl.language] = bl);
    state.__currentMaxBookLangDisplayOrder = Math.max(0, ...bls.map(bl => bl.display_order));

    //Set the "translation" for the source language.
    const sentenceText = b.sentence_set.map(s => s.text).join('\n');
    b.booklanguage_set
        .filter(bl => bl.language === b.source_language)
        .forEach(bl => bl.translation = sentenceText);

    fixDuplicateDisplayOrders(state);
}

//Really shouldn't happen.
//But it did. Hopefully due to bugs I fixed already.
function fixDuplicateDisplayOrders(state: BookEditorState)
{
    const arr = Object.values(state.__allBookLanguages);
    const groups = Object.values(groupBy(arr, bl => bl.display_order));
    for (const g of groups.filter(g => g.length > 1))
    {
        const dupes = g.slice(1).reverse();
        for (const bl of dupes)
        {
            arr
                .filter(bl2 => bl2.display_order > bl.display_order)
                .forEach(bl2 => bl2.display_order++);

            bl.display_order++;
        }
    }
}

function getBookLanguageByLangId(book: Book, id: string)
{
    return book.booklanguage_set.find(bl => bl.language === id);
}

const initialState = 
{
    /** Object containing data for the book currently being edited.*/
    formBook: copyRestObject(new Book(), Book),

    /** 
     * Object containing every BookLanguage that has been added to the book since we started editing,
     * even if it was then removed. If the language is added back, we get it from here, thus not losing its settings.
     */
    __allBookLanguages: {} as {[langId: string]: BookLanguage},
    __currentMaxBookLangDisplayOrder: 0,

};

const bookEditFormSlice = createSlice(
{
    name: 'books',
    initialState: initialState,
    reducers:
    {
        /** Completely clear the form's state. */
        init: function(state)
        {
            Object.assign(state, deepCopyObject(initialState));
        },
        sortAndSyncBookLanguages: function(state)
        {
            sortAndSyncBookLanguages(state);
        },
        setFormBook: function(state, action: PayloadAction<Book>)
        {
            state.formBook = action.payload;
        },
        setTitle: function(state, action: PayloadAction<string>)
        {
            state.formBook.title = action.payload;
        },
        setSourceText: function(state, action: PayloadAction<string>)
        {
            state.formBook.source_text = action.payload;
        },
        setIsHTML: function(state, action: PayloadAction<boolean>)
        {
            state.formBook.is_html = action.payload;
        },
        setSourceLanguage: function(state, action: PayloadAction<string>)
        {
            //First, unset the text for the old language.
            const b = state.formBook;
            b.booklanguage_set
                .filter(bl => bl.language === b.source_language)
                .forEach(bl => bl.translation = '');

            state.formBook.source_language = action.payload;

            //Then, set it for the new.            
            sortAndSyncBookLanguages(state);
        },
        setAbbrs: function(state, action: PayloadAction<string>)
        {            
            state.formBook.abbreviations = action.payload;
        },
        setNonTextElements: function(state, action: PayloadAction<string>)
        {            
            state.formBook.non_text_elements = action.payload;
        },
        setSkippedElements: function(state, action: PayloadAction<string>)
        {            
            state.formBook.skipped_elements = action.payload;
        },
        setPageSize: function(state, action: PayloadAction<string>)
        {
            state.formBook.page_size = parseInt(action.payload);
        },
        setTranslation: function(state, action: PayloadAction<[string, string]>)
        {
            const langId = action.payload[0];
            const text = action.payload[1];
            const bl = getBookLanguageByLangId(state.formBook, langId);

            if (bl)
            {
                bl.translation = text;
            }
            
            sortAndSyncBookLanguages(state);
        },
        /**
         * Moves book language.
         * First number is original position, second number is new position.
         * Inserted BEFORE the language at the new position if the movement was UP.
         * Inserted AFTER the language at the new position if the movement was DOWN.
         */
        moveBookLanguage: function(state, action: PayloadAction<[number, number]>)
        {
            const bls = state.formBook.booklanguage_set;

            //Make sure both containers refer to the same bl objects.
            //Even though I thought they already do. Shenanigans.
            bls.forEach((bl) => state.__allBookLanguages[bl.language] = bl);
            
            const abls = Object.values(state.__allBookLanguages);

            //Old index of the lang in the table.
            //Distinct from display_order, which may have gaps because languages can get
            //deleted from the table.
            const oldpos = action.payload[0];

            //New index of the lang in the table.
            const newindex = action.payload[1];

            if (bls[oldpos] && bls[newindex])
            {
                const newDO = bls[newindex].display_order;

                //If movement was up, increment all positions at and after the new index, 
                //so this language precedes them.
                if (oldpos > newindex)
                {
                    const abls2 = abls.filter(bl => bl.display_order >= newDO);
                    abls2.forEach(bl => bl.display_order++);
                }

                
                //If movement was down, decrement display_order of all positions
                //at and before the new index, so this language follows them.
                if (oldpos < newindex)
                {
                    //I want the item moved to the item AFTER the one currently at newindex.
                    const abls2 = abls.filter(bl => bl.display_order <= newDO);
                    abls2.forEach(bl => bl.display_order--);
                }
                

                bls[oldpos].display_order = newDO
            }
            
            sortAndSyncBookLanguages(state);
        },
        addBookLanguage: function(state, action: PayloadAction<string>)
        {
            const langId = action.payload;
            addLanguageToBook(langId, state);
            
            sortAndSyncBookLanguages(state);
        },
        deleteBookLanguage: function(state, action: PayloadAction<number>)
        {
            const pos = action.payload;
            const book = state.formBook;
            book.booklanguage_set.splice(pos, 1);

            sortAndSyncBookLanguages(state);
        },
        setBookLanguages: function(state, action: PayloadAction<string[]>)
        {
            state.formBook.booklanguage_set = [];
            for (const langId of action.payload)
            {
                addLanguageToBook(langId, state);
            }

            sortAndSyncBookLanguages(state);
        }
    },
});

export const bookEditorReducer = bookEditFormSlice.reducer;
export const bookEditorActions = bookEditFormSlice.actions;
type BookEditorState = typeof initialState;